import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Shards } from '@sorare/core/src/atoms/icons/Shards.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography/index.tsx';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { PickerOption } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { PickerOptionsGrid } from '#src/components/pickers/PickerOptionsGrid.tsx';

import type { LeaderboardPicker_so5Leaderboard } from './__generated__/index.graphql.ts';

const GroupIcon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;

  &.md {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    margin-top: var(--intermediate-unit);
  }
`;

const GridIcon = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

type Props = {
  leaderboards: readonly LeaderboardPicker_so5Leaderboard[];
  selectedLeaderboard: LeaderboardPicker_so5Leaderboard;
  onSelect: (slug: string) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

export const LeaderboardPicker = ({
  leaderboards,
  selectedLeaderboard,
  onSelect,
  size = 'sm',
}: Props) => {
  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={selectedLeaderboard.format.title}
          size={size}
          icon={
            selectedLeaderboard.format.iconUrl ? (
              <GroupIcon
                className={size === 'md' ? 'md' : undefined}
                src={selectedLeaderboard.format.iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation="up"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => {
        return (
          <>
            <PickerHeader onClose={closeDropdown}>
              <FormattedMessage defaultMessage="Choose Format" />
            </PickerHeader>
            <PickerOptionsGrid>
              {leaderboards.map(leaderboard => {
                const entryRule = leaderboard?.displayedTypedRules.find(
                  isTypeFn('So5EntryItemRule')
                );
                const entryItem = entryRule?.entryItem;
                return (
                  <PickerOption
                    key={leaderboard.slug}
                    className={classNames({
                      selected: leaderboard.slug === selectedLeaderboard.slug,
                    })}
                    onClick={() => {
                      onSelect(leaderboard.slug);
                      closeDropdown();
                    }}
                  >
                    <Vertical center>
                      <BrandS color="var(--c-white)">
                        {leaderboard.format.title}
                      </BrandS>
                      {!!leaderboard.format.iconUrl && (
                        <GridIcon
                          src={leaderboard.format.iconUrl}
                          alt={leaderboard.format.title}
                        />
                      )}
                      {!!entryItem && (
                        <Horizontal gap={0.5}>
                          <Shards color="var(--c-arena-primary)" size={2} />
                          <BrandXS color="var(--c-arena-primary)">
                            {entryItem?.quantity}
                          </BrandXS>
                        </Horizontal>
                      )}
                    </Vertical>
                  </PickerOption>
                );
              })}
            </PickerOptionsGrid>
          </>
        );
      }}
    </Dropdown>
  );
};

LeaderboardPicker.fragments = {
  so5Leaderboard: gql`
    fragment LeaderboardPicker_so5Leaderboard on So5Leaderboard {
      id
      slug
      format {
        iconUrl
        title
      }
      displayedTypedRules {
        id
        key
        ... on So5EntryItemRule {
          id
          entryItem {
            id
            ... on So5CardShardsEntryItem {
              id
              quantity
              rarity
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<LeaderboardPicker_so5Leaderboard>,
};
